import { useState, type FormEvent } from 'react'
import type { UnmappedWord, UpstreamAttributeView } from '@fitting-room/contracts'
import { useRuleSyncWord, useSyncWords, type SyncWordRuling } from '../../../lib/adminQueries'
import { CATALOG_LABEL, attributeNoun, styleCount } from './words'

/**
 * "New words from Airtable" — the scan's wordings nobody has ruled on, each
 * with the three answers an owner can give, one click apiece.
 *
 * This replaces a sync that stopped dead on the first unknown neckline and
 * told the owner to edit a source file. Nothing here blocks the apply: a gown
 * that uses a new word syncs without that one detail, and the panel is how it
 * gets the detail back. After every ruling the page scans again
 * (`onRuled`), so the word leaves the list the moment it is answered — the
 * same read the apply will make, rather than a promise that it will.
 */
export function NewWordsPanel({
  staffToken,
  words,
  onRuled,
}: {
  readonly staffToken: string | null
  readonly words: readonly UnmappedWord[]
  readonly onRuled: () => void
}) {
  const vocabulary = useSyncWords(staffToken)
  const attributes = vocabulary.data?.attributes ?? []

  return (
    <section className="st-notice mb-6" aria-labelledby="new-words-title">
      <h2 id="new-words-title" className="font-medium">
        New words from Airtable
      </h2>
      <p className="mt-1 max-w-2xl">
        Airtable uses {words.length === 1 ? 'a word' : `${words.length} words`} we don&rsquo;t have a match for yet.
        Gowns that use one still sync, just without that detail until you match it. Match it to one of ours, add
        it as a new one, or ignore it if it says nothing worth keeping. Nothing in Airtable changes, and you can
        change your answer later on the Catalog page.
      </p>
      {vocabulary.isError ? (
        <p className="st-alert mt-3">Couldn&rsquo;t load this boutique&rsquo;s options. Scan again to retry.</p>
      ) : null}

      <ul className="mt-3 space-y-2">
        {words.map((word) => (
          <WordRow
            key={`${word.catalog}-${word.attribute}-${word.wording}`}
            staffToken={staffToken}
            word={word}
            attribute={attributes.find((a) => a.key === word.attribute)}
            onRuled={onRuled}
          />
        ))}
      </ul>
    </section>
  )
}

function WordRow({
  staffToken,
  word,
  attribute,
  onRuled,
}: {
  readonly staffToken: string | null
  readonly word: UnmappedWord
  readonly attribute: UpstreamAttributeView | undefined
  readonly onRuled: () => void
}) {
  const rule = useRuleSyncWord(staffToken)
  const [label, setLabel] = useState(word.wording)
  const noun = attributeNoun(attribute, word.attribute)
  const busy = rule.isPending || !attribute

  const send = (ruling: SyncWordRuling) => rule.mutate(ruling, { onSuccess: onRuled })

  const addOption = (event: FormEvent) => {
    event.preventDefault()
    const trimmed = label.trim()
    if (!trimmed) return
    send({ kind: 'new-option', body: { attribute: word.attribute, wording: word.wording, label: trimmed } })
  }

  return (
    <li className="st-card px-4 py-3" style={{ color: 'var(--st-ink)' }}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="st-eyebrow">{attribute?.label ?? noun}</span>
        <span className="font-medium">&ldquo;{word.wording}&rdquo;</span>
        <span className="st-meta text-xs">{CATALOG_LABEL[word.catalog]}</span>
      </div>
      <details className="st-meta mt-1 text-xs">
        <summary className="cursor-pointer">{styleCount(word)}</summary>
        <p className="mt-1 break-words">{word.styles.join(', ')}</p>
      </details>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex min-w-0 max-w-full flex-col gap-1">
          <span className="st-eyebrow">Same as</span>
          <select
            className="st-select min-w-0 max-w-full"
            aria-label={`Match "${word.wording}" to an existing ${noun}`}
            value=""
            disabled={busy}
            onChange={(event) => {
              const value = event.target.value
              if (value) send({ kind: 'set', body: { attribute: word.attribute, wording: word.wording, rule: { action: 'map', value } } })
            }}
          >
            <option value="">Choose a {noun}…</option>
            {(attribute?.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <form onSubmit={addOption} className="flex min-w-0 max-w-full flex-wrap items-end gap-2">
          <label className="flex min-w-0 max-w-full flex-col gap-1">
            <span className="st-eyebrow">Or a new {noun}, called</span>
            <input
              className="st-input min-w-0 max-w-full"
              aria-label={`Name of the new ${noun}`}
              value={label}
              maxLength={60}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <button type="submit" className="st-btn-ghost" disabled={busy || !label.trim()}>
            Add as a new {noun}
          </button>
        </form>

        <button
          type="button"
          className="st-btn-quiet"
          disabled={busy}
          onClick={() => send({ kind: 'set', body: { attribute: word.attribute, wording: word.wording, rule: { action: 'ignore' } } })}
        >
          Ignore
        </button>
      </div>

      {rule.isPending ? <p className="st-meta mt-2 text-xs">Saving, then scanning again…</p> : null}
      {rule.isError ? (
        <p className="st-alert mt-2" role="alert">
          {rule.error.message}
        </p>
      ) : null}
    </li>
  )
}
